// PieChartComponent.tsx
import React from "react";
import {
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";

interface PieChartProps {
  data: any[];
  onSliceClick: (sliceKey: any) => void;
  selectedSlice: string | null;
  loading: boolean;
}

const COLORS = [
  "#3b82f6", // Blue
  "#ec4899", // Pink
  "#10b981", // Emerald
  "#f59e0b", // Amber
  "#8b5cf6", // Purple
  "#06b6d4", // Cyan
  "#f97316", // Orange
  "#6366f1", // Indigo
  "#84cc16", // Lime
  "#d946ef", // Fuchsia
  "#14b8a6", // Teal
  "#ef4444", // Red
];

const getItemColor = (index: number) => {
  if (index < COLORS.length) {
    return COLORS[index];
  }
  const hue = (index * 137.5) % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

const PieChart: React.FC<PieChartProps> = ({
  data,
  onSliceClick,
  selectedSlice,
  loading,
}) => {
  const formattedData = data.map((item) => ({
    key: item.key,
    name: item.label,
    value: item.count,
  }));

  const handleContainerClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === "svg" ||
      target.classList.contains("recharts-wrapper") ||
      target.tagName === "DIV"
    ) {
      if (selectedSlice !== null) {
        onSliceClick(null);
      }
    }
  };

  return (
    <div
      className="bg-white p-6 rounded-lg shadow-sm flex flex-col min-h-[300px] relative cursor-pointer"
      onClick={handleContainerClick}
    >
      {/* Header με Τίτλο & Κουμπί Επαναφοράς/Εμφάνισης Όλων */}
      <div className="flex justify-between items-center w-full mb-4">
        <h3 className="text-sm font-semibold text-gray-700">
          Κατανομή Δεδομένων (Pie Chart)
        </h3>

        {/* Εμφανίζεται ΜΟΝΟ όταν υπάρχει επιλεγμένο slice */}
        {selectedSlice && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // Αποφυγή triggering του container click
              onSliceClick(null);
            }}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 px-2.5 py-1 rounded transition-colors cursor-pointer"
          >
            ↺ Εμφάνιση όλων
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500 text-sm">Φόρτωση γραφήματος...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500 text-sm">
            Δεν βρέθηκαν δεδομένα για τα επιλεγμένα φίλτρα.
          </p>
        </div>
      ) : (
        <div className="flex flex-row items-center justify-center w-full h-[250px] gap-8">
          
          {/* Γράφημα */}
          <div className="w-1/2 h-full">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={formattedData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  nameKey="name"
                  onClick={(entry, index, e) => {
                    e.stopPropagation();
                    if (selectedSlice === entry.key) {
                      onSliceClick(null);
                    } else {
                      onSliceClick(entry.key);
                    }
                  }}
                  cursor="pointer"
                >
                  {formattedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getItemColor(index)}
                      opacity={
                        selectedSlice && selectedSlice !== entry.key ? 0.4 : 1
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>

          {/* Δεξιά στήλη - Legend */}
          <div className="flex flex-col justify-center gap-2 pl-6 border-l border-gray-100 min-w-[200px] max-h-full overflow-y-auto">
            {formattedData.map((item, index) => {
              const itemColor = getItemColor(index);
              const isSelected = selectedSlice === item.key;
              const isFaded = selectedSlice && !isSelected;

              return (
                <div
                  key={item.key ?? index}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSliceClick(isSelected ? null : item.key);
                  }}
                  className={`flex items-center justify-between gap-4 px-3 py-1.5 rounded border transition-all cursor-pointer ${
                    isFaded ? "opacity-30 hover:opacity-70" : "opacity-100"
                  }`}
                  style={{
                    borderColor: `${itemColor}40`,
                    backgroundColor: `${itemColor}10`,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: itemColor }}
                    />
                    <span
                      className="text-xs font-semibold"
                      style={{ color: itemColor }}
                    >
                      {item.name}:
                    </span>
                  </div>
                  <span
                    className="text-xs font-bold"
                    style={{ color: itemColor }}
                  >
                    {item.value}
                  </span>
                </div>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
};

export default PieChart;